import { useEffect, useMemo, useState } from 'react';

import { useApiKeyClientServiceQuery } from './react-query/useApiKeyClientServiceQuery';

const checkIsScriptTagLoaded = () => {
  return !!window.google && !!window.google.maps && !!window.google.maps.places;
};
const loadScriptByUrl = async (url: string) => {
  await new Promise<void>((resolve) => {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length; i--; ) {
      if (scripts[i].src == url) {
        resolve(); // Already have that url assigned to a script, don't add again. Instead just
        // call the callback and have it go on about its business in wherever it was called from
        return;
      }
    }
    const script = document.createElement('script') as any;
    script.type = 'text/javascript';

    if (script.readyState) {
      script.onreadystatechange = () => {
        if (script.readyState === 'loaded' || script.readyState === 'complete') {
          script.onreadystatechange = null;
          resolve();
        }
      };
    } else {
      script.onload = () => resolve();
    }

    script.src = url;
    document.getElementsByTagName('head')[0].appendChild(script);
  });
};

export const useLoadGoogleMapsPlaces = ({ enabled }: { enabled?: boolean } = {}) => {
  const googleApiKeyQuery = useApiKeyClientServiceQuery({
    filter: { textId: 'google_maps' },
    select(data) {
      return data?.apiKey;
    },
    enabled,
  });

  const [isScriptTagLoaded, setIsScriptTagLoaded] = useState(checkIsScriptTagLoaded());

  useEffect(() => {
    if (googleApiKeyQuery.isSuccess && !isScriptTagLoaded) {
      const googleLibURL = `https://maps.googleapis.com/maps/api/js?key=${googleApiKeyQuery.data}&libraries=places`;
      loadScriptByUrl(googleLibURL).then(() => {
        setIsScriptTagLoaded(true);
      });
    }
  }, [googleApiKeyQuery.data, googleApiKeyQuery.isSuccess, isScriptTagLoaded]);

  return useMemo(() => {
    return isScriptTagLoaded && checkIsScriptTagLoaded() ? window.google.maps.places : null;
  }, [isScriptTagLoaded]);
};

export type AddressState = {
  components: google.maps.GeocoderAddressComponent[];
} | null;
export const useAddressAutocomplete = ({
  googleAutocomplete,
}: {
  googleAutocomplete: InstanceType<typeof window.google.maps.places.Autocomplete> | null;
}) => {
  const [address, setAddress] = useState<AddressState>(null);
  useEffect(() => {
    if (!googleAutocomplete) return;

    googleAutocomplete.addListener('place_changed', () => {
      const place = googleAutocomplete.getPlace();
      if (!place.geometry) return;
      if (!place.address_components) return;
      setAddress({ components: place.address_components });
    });
  }, [googleAutocomplete]);

  return useMemo<[AddressState, React.Dispatch<AddressState>]>(() => {
    return [address, setAddress];
  }, [address, setAddress]);
};
